import { useCallback, useEffect, useRef } from "react";

interface UseNotificationSoundOptions {
  muted: boolean;
}

/**
 * Hook that plays a short notification chime when called.
 * Respects gallery muted setting and browser autoplay policy.
 * Sound only plays after the user has interacted with the page.
 */
export function useNotificationSound({ muted }: UseNotificationSoundOptions) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasInteractedRef = useRef(false);

  // Track user interaction for browser autoplay policy
  useEffect(() => {
    const markInteracted = () => {
      hasInteractedRef.current = true;
    };
    window.addEventListener("click", markInteracted, { once: true });
    window.addEventListener("keydown", markInteracted, { once: true });
    return () => {
      window.removeEventListener("click", markInteracted);
      window.removeEventListener("keydown", markInteracted);
    };
  }, []);

  const play = useCallback(() => {
    if (muted || !hasInteractedRef.current) return;

    try {
      // Lazy-load Audio to avoid blocking page load
      if (!audioRef.current) {
        audioRef.current = new Audio("/sounds/notification.mp3");
        audioRef.current.volume = 0.5;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Graceful fallback: browser blocked autoplay
      });
    } catch {
      // No-op: Audio API not available
    }
  }, [muted]);

  return play;
}
